import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import LoaderContainer from "~Common/Page/LoaderContainer";
import { AddPersonRolesModal } from "~components/AddPersonRolesModal";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { RoleStatuses } from "~enums/roleStatuses.ts";
import { useFetchRoles } from "~hooks/apiHooks/useFetchRoles.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import useFetchEntity from "~pages/Roster/hooks/useFetchEntity";
import { useStaffService } from "~pages/Staff/hooks/useStaffService.ts";
import { StaffTableColumns } from "~pages/Staff/StaffListPage/enums/staffTableColumns.ts";
import { useGetStaffTableColumns } from "~pages/Staff/StaffListPage/hooks/useGetStaffTableColumns.tsx";
import type { StaffListItem } from "~pages/Staff/StaffListPage/types/staffListItem.ts";
import { transformStaffResponse } from "~pages/Staff/StaffListPage/utilities/transformStaffResponse.ts";
import type { UpdateStaffRole } from "~pages/Staff/types/staffRole.ts";

const ROLE_STATUSES = Object.values(RoleStatuses);
const DEFAULT_SORTER = { columnKey: StaffTableColumns.NameFullLocal, order: "ascend" as const };

const StaffListPage = () => {
  const { entityId, customerId, sport, seasonId, organizationId } = useParams();

  if (!entityId) {
    throw new Error("entityId not found in route params");
  }

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useUserPermissions();
  const { seasonStaffRoles, uiConfiguration } = useSportConfiguration();
  const paginationConfig = useHandlePagination(10);
  const { resetPagination } = paginationConfig;

  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { data: entity, isLoading: isEntityLoading } = useFetchEntity(entityId);

  const listParams = useMemo(
    () => ({
      entityId,
      role: seasonStaffRoles.join(","),
      hideNull: "true",
      limit: "1000",
      nameFullLocalContains: searchText,
      offset: "0",
    }),
    [entityId, searchText, seasonStaffRoles],
  );

  const { data: staff, isFetching, refetch } = useFetchRoles(listParams, transformStaffResponse, [entityId]);

  const { addStaffRole, isAddPending, updateStaffRole, deleteStaffRole, isDeletePending } = useStaffService();

  const invalidateStaff = () => queryClient.invalidateQueries({ queryKey: ["roles", sport, organizationId, entityId] });

  const handleSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchText(value);
    },
    [resetPagination],
  );

  const handleUpdateRole = async (row: StaffListItem & DataTableRow, update: Partial<Pick<UpdateStaffRole, "role" | "status">>) => {
    row.disabled = true;
    try {
      const result = await updateStaffRole({
        roleId: row.roleId,
        ...(row.entityId ? { entityId: row.entityId } : {}),
        ...update,
      });

      if (result) {
        await refetch();
      }
    } finally {
      row.disabled = false;
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    const deleted = await deleteStaffRole(roleId);

    if (deleted) {
      await refetch();
    }
  };

  const columns = useGetStaffTableColumns({
    massEditMode,
    onUpdateRole: handleUpdateRole,
    roleStatuses: ROLE_STATUSES,
    staffRoles: seasonStaffRoles,
  });

  const pageTitle = `${entity?.nameFullLocal || entityId} ${formatMessage({ id: "staff" })}`;
  const canAddStaff =
    hasPermission(PermissionNames.ROLES, PermissionOperations.CREATE) && hasPermission(PermissionNames.PERSON, PermissionOperations.READ);

  const getPersonUrl = (personId: string) => `/${customerId}/admin/${sport}/${organizationId}/persons/manage/${personId}`;

  return (
    <LoaderContainer loading={isEntityLoading}>
      <Page
        title={pageTitle}
        tabTitle={pageTitle}
        onBack={() => {
          navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities`);
        }}
      >
        {canAddStaff && (
          <AddPersonRolesModal
            open={isAddModalOpen}
            setOpen={setIsAddModalOpen}
            roles={seasonStaffRoles}
            statuses={ROLE_STATUSES}
            entityId={entityId}
            onAddRole={addStaffRole}
            isAddPending={isAddPending}
            refetch={() => {
              void refetch();
            }}
          />
        )}
        <DataTable<StaffListItem>
          handleRefresh={async () => {
            await invalidateStaff();
          }}
          columns={columns}
          rowActions={{
            base: {
              manage: {
                onClick: (row) => navigate(getPersonUrl(row.personId)),
              },
              delete: {
                onClick: (row) => handleDeleteRole(row.roleId),
                itemName: (row) => row.nameFullLocal || row.personId,
                loading: isDeletePending,
              },
            },
          }}
          rowNavigation={{
            getNavigateUrl: (row) => getPersonUrl(row.personId),
          }}
          rowKey="roleId"
          rowNameKey="nameFullLocal"
          dataSource={staff ?? []}
          searchConfig={{
            handleSearch,
            searchText,
          }}
          isDataLoading={isFetching || isDeletePending}
          handleAdd={canAddStaff ? () => setIsAddModalOpen(true) : undefined}
          addButtonIntlKey="staff.add"
          onMassEditModeChangedHandler={setMassEditMode}
          massEditMode={massEditMode}
          permissionNames={[PermissionNames.ROLES]}
          columnChooserLocalStorageKey={`${sport}.tables.settings.columns.staff`}
          columnSortConfig={{
            sorterLocalStorageKey: `${sport}.tables.settings.sort.staff`,
            defaultSorter: DEFAULT_SORTER,
          }}
          dataTableUiConfiguration={uiConfiguration.pages.staff.listPage}
          paginationConfig={{
            ...paginationConfig,
            total: staff?.length,
          }}
        />
      </Page>
    </LoaderContainer>
  );
};

export default StaffListPage;
